
import { defineComponent, computed } from 'vue'
import type { Position, ColorMode, DividendInfo } from '@/types/arkOne'
import { formatPercentage, formatPrice, getValueColor, formatDividendInfo } from '@/utils/arkOneUtils'
import Nodata from '@/components/page/nodata.vue'
import StockCodeDisplay from './StockCodeDisplay.vue'

const __default__ = defineComponent({
  name: 'PositionDetailsTable',
  components: {
    Nodata,
    StockCodeDisplay
  },
  props: {
    positions: {
      type: Array as () => Position[],
      required: true
    },
    colorMode: {
      type: String as () => ColorMode,
      default: 'red-rise-green-fall'
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    // 主题：'dark' | 'light'
    theme: {
      type: String,
      default: 'dark'
    },
    // 语言支持：'zh-Hans' 简体中文，'zh-Hant' 繁体中文
    language: {
      type: String,
      default: 'zh-Hans'
    },
    // 港股延时
    hkDelay: {
      type: Boolean,
      default: false
    },
    // 美股延时
    usDelay: {
      type: Boolean,
      default: false
    }
  },
  emits: ['orderClick', 'rowDoubleClick'],
  setup(props, { emit }) {
    // 语言相关
    const isTraditional = computed(() => props.language === 'zh-Hant')
    
    console.log('[PositionDetailsTable] props.language:', props.language)
    console.log('[PositionDetailsTable] isTraditional:', isTraditional.value)
    
    // 翻译函数
    const t = (key: string): string => {
      const translations: Record<string, Record<string, string>> = {
        'tableHeaders': {
          'sequence': isTraditional.value ? '序' : '序',
          'code': isTraditional.value ? '代碼' : '代码',
          'name': isTraditional.value ? '名稱' : '名称',
          'simulatedReturn': isTraditional.value ? '模擬收益率' : '模拟收益率',
          'currentPrice': isTraditional.value ? '現價' : '现价',
          'cost': isTraditional.value ? '成本' : '成本',
          'holdingPercentage': isTraditional.value ? '持倉比例' : '持仓比例',
          'annualizedDividendYield': isTraditional.value ? '年化股息率' : '年化股息率',
          'openingDate': isTraditional.value ? '建倉日期' : '建仓日期',
          'dividend': isTraditional.value ? '派息' : '派息',
          'operation': isTraditional.value ? '操作' : '操作'
        },
        'common': {
          'title': isTraditional.value ? '持倉明細' : '持仓明细',
          'trade': isTraditional.value ? '下單' : '下单',
          'noData': isTraditional.value ? '暫無數據' : '暂无数据',
          'dividendLabel': isTraditional.value ? '派息：' : '派息：'
        }
      }
      
      const [category, subKey] = key.split('.')
      const result = translations[category]?.[subKey] || key
      console.log(`[PositionDetailsTable] t('${key}') = '${result}'`)
      return result
    }
    // 有派息信息的持仓
    const positionsWithDividend = computed(() => {
      return props.positions.filter(p => p.latestDividend)
    })

    const showDividendInfo = computed(() => {
      return positionsWithDividend.value.length > 0
    })

    // 获取收益率颜色
    const getReturnColor = (value: number): string => {
      return getValueColor(value, props.colorMode)
    }

    const formatPercentagePlain = (value: number, decimals: number = 2): string => {
      return `${value.toFixed(decimals)}%`
    }

    // 格式化派息信息
    const formatDividend = (dividend: DividendInfo): string => {
      return formatDividendInfo(
        dividend.date,
        dividend.sharesPerDividend,
        dividend.amountPerShare,
        dividend.currency
      )
    }

    // 处理下单按钮点击
    const handleOrderClick = (position: Position) => {
      emit('orderClick', position)
    }

    // 处理行双击
    const handleRowDoubleClick = (position: Position) => {
      emit('rowDoubleClick', position)
    }

    return {
      positionsWithDividend,
      showDividendInfo,
      formatPercentage,
      formatPercentagePlain,
      formatPrice,
      getReturnColor,
      formatDividend,
      handleOrderClick,
      handleRowDoubleClick,
      t
    }
  }
})

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "6c47ef0d": (_ctx.theme === "light" ? "#ffffff" : "#111424"),
  "99f6eb9c": (_ctx.theme === "light" ? "#000000" : "#ffffff"),
  "4e04b4be": (_ctx.theme === "light" ? "#f0f0f0" : "#0a0a0a"),
  "1859fcd2": (_ctx.theme === "light" ? "#666666" : "#999999"),
  "1ec998fa": (_ctx.theme === "light" ? "#f5f7fa" : "#0f1426"),
  "86c33cda": (_ctx.theme === "light" ? "#e4e7ed" : "#1f1f1f"),
  "09c81aba": (_ctx.theme === "light" ? "rgba(0,0,0,0.04)" : "rgba(0,0,0,0.35)"),
  "3a657018": (_ctx.theme === "light" ? "#f5f5f5" : "rgba(255, 255, 255, 0.05)"),
  "98cfb5dc": (_ctx.theme === "light" ? "#333333" : "#ffffff"),
  "612e9c5c": (_ctx.theme === "light" ? "#e4e7ed" : "#333333"),
  "74f0acd2": (_ctx.theme === "light" ? "#999999" : "#666666"),
  "75506af4": (_ctx.theme === "light" ? "#f5f7fa" : "rgba(255, 255, 255, 0.05)")
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__